import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * /upsell-rules — Sam-managed struggle-upsell rules (spec §3.9 + §4.5).
 *
 * Each row is a (grade, difficulty_level, fail_threshold, coupon_id)
 * tuple that drives the server-side struggle-upsell predicate. Sam
 * picks a coupon (any active coupon in the coupons table) and a
 * difficulty level per grade Sam wants to promote the previous grade
 * on. Multiple rules per grade are allowed — different tiers per
 * difficulty level.
 *
 * Follows the CouponsScreen conventions: antd Table + create/edit modal,
 * PATCH-only for `fail_threshold` + `coupon_id` (grade + difficulty are
 * identity and require delete+recreate to change).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Empty, Form, InputNumber, Modal, Popconfirm, Select, Table, Typography, } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { createUpsellRule, deleteUpsellRule, listCoupons, listGrades, listUpsellRules, updateUpsellRule, } from '../api';
import { PageHeader } from '../components/PageHeader';
import { notifySuccess } from '../components/errors';
const { Text } = Typography;
export default function UpsellRulesScreen() {
    const [rows, setRows] = useState([]);
    const [grades, setGrades] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const reload = useCallback(async () => {
        setLoading(true);
        try {
            // Load rules alongside grades + coupons so the pickers can render
            // by name/code rather than by opaque id.
            const [r, g, c] = await Promise.all([
                listUpsellRules(), listGrades(), listCoupons(),
            ]);
            setRows(r);
            setGrades(g);
            setCoupons(c);
        }
        catch { /* client auto-surfaces */ }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { void reload(); }, [reload]);
    const cols = useMemo(() => [
        { title: 'כיתה', dataIndex: 'grade_name', width: 140, render: (v) => _jsx(Text, { strong: true, children: v }) },
        {
            title: 'רמת קושי', dataIndex: 'difficulty_level', width: 100,
            render: (v) => _jsx(Text, { children: v }),
        },
        {
            title: 'סף כשלונות', dataIndex: 'fail_threshold', width: 120,
            render: (v) => _jsx(Text, { children: v }),
        },
        {
            title: 'קופון', key: '__coupon', width: 200,
            render: (_, r) => (_jsxs("span", { children: [_jsx(Text, { code: true, children: r.coupon_code }), ' ', _jsxs(Text, { type: "secondary", children: ["(", r.coupon_discount_percent, "%)"] })] })),
        },
        {
            title: 'פעולות', width: 130, fixed: 'right',
            render: (_, r) => (_jsxs("span", { children: [_jsx(Button, { size: "small", icon: _jsx(EditOutlined, {}), onClick: () => setEditing(r), style: { marginInlineEnd: 8 } }), _jsx(Popconfirm, { title: "\u05DC\u05DE\u05D7\u05D5\u05E7 \u05DB\u05DC\u05DC?", okText: "\u05DE\u05D7\u05E7", cancelText: "\u05D1\u05D8\u05DC", onConfirm: async () => {
                            try {
                                await deleteUpsellRule(r.id);
                                notifySuccess('כלל נמחק');
                                reload();
                            }
                            catch { /* client auto-surfaces */ }
                        }, children: _jsx(Button, { size: "small", danger: true, icon: _jsx(DeleteOutlined, {}) }) })] })),
        },
    ], [reload]);
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: "\u05DB\u05DC\u05DC\u05D9 Upsell", breadcrumb: [{ label: 'ניהול' }, 'כללי Upsell'], actions: _jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => setCreateOpen(true), children: "\u05DB\u05DC\u05DC \u05D7\u05D3\u05E9" }) }), _jsx(Card, { children: rows.length === 0 && !loading
                    ? (_jsx(Empty, { description: "\u05D0\u05D9\u05DF \u05E2\u05D3\u05D9\u05D9\u05DF \u05DB\u05DC\u05DC\u05D9 upsell", image: Empty.PRESENTED_IMAGE_SIMPLE, children: _jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => setCreateOpen(true), children: "\u05DB\u05DC\u05DC \u05D7\u05D3\u05E9" }) }))
                    : (_jsx(Table, { size: "small", rowKey: "id", pagination: false, loading: loading, dataSource: rows, columns: cols })) }), _jsx(RuleCreateModal, { open: createOpen, onClose: () => setCreateOpen(false), onDone: () => { setCreateOpen(false); reload(); }, grades: grades, coupons: coupons }), _jsx(RuleEditModal, { rule: editing, onClose: () => setEditing(null), onDone: () => { setEditing(null); reload(); }, coupons: coupons })] }));
}
function RuleCreateModal({ open, onClose, onDone, grades, coupons, }) {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const submit = async () => {
        let v;
        try {
            v = await form.validateFields();
        }
        catch {
            return;
        }
        setSubmitting(true);
        try {
            await createUpsellRule({
                grade_id: Number(v.grade_id),
                difficulty_level: Number(v.difficulty_level),
                fail_threshold: Number(v.fail_threshold),
                coupon_id: Number(v.coupon_id),
            });
            notifySuccess('כלל נוצר');
            form.resetFields();
            onDone();
        }
        catch { /* client auto-surfaces */ }
        finally {
            setSubmitting(false);
        }
    };
    return (_jsx(Modal, { title: "\u05D9\u05E6\u05D9\u05E8\u05EA \u05DB\u05DC\u05DC Upsell", open: open, onCancel: onClose, onOk: submit, okText: "\u05E6\u05D5\u05E8", cancelText: "\u05D1\u05D8\u05DC", confirmLoading: submitting, destroyOnHidden: true, children: _jsxs(Form, { form: form, layout: "vertical", preserve: false, initialValues: { difficulty_level: 1, fail_threshold: 2 }, children: [_jsx(Form.Item, { name: "grade_id", label: "\u05DB\u05D9\u05EA\u05D4", rules: [{ required: true, message: 'כיתה נדרשת' }], children: _jsx(Select, { placeholder: "\u05D1\u05D7\u05E8 \u05DB\u05D9\u05EA\u05D4", options: grades.map((g) => ({ value: g.id, label: g.name })) }) }), _jsx(Form.Item, { name: "difficulty_level", label: "\u05E8\u05DE\u05EA \u05E7\u05D5\u05E9\u05D9", tooltip: "\u05D4\u05E8\u05DE\u05D4 \u05E9\u05D1\u05D4 \u05E0\u05E1\u05E4\u05E8\u05D9\u05DD \u05D4\u05DB\u05E9\u05DC\u05D5\u05E0\u05D5\u05EA (1..10)", rules: [{ required: true, message: 'רמת קושי נדרשת' }], children: _jsx(InputNumber, { min: 1, max: 10, style: { width: '100%' } }) }), _jsx(Form.Item, { name: "fail_threshold", label: "\u05E1\u05E3 \u05DB\u05E9\u05DC\u05D5\u05E0\u05D5\u05EA", tooltip: "\u05DE\u05E1\u05E4\u05E8 \u05D4\u05DB\u05E9\u05DC\u05D5\u05E0\u05D5\u05EA \u05D1\u05E8\u05DE\u05D4 \u05D6\u05D5 \u05DC\u05E4\u05E0\u05D9 \u05E9\u05D4\u05BEupsell \u05DE\u05D5\u05E6\u05D2", rules: [{ required: true, message: 'סף כשלונות נדרש' }], children: _jsx(InputNumber, { min: 1, max: 50, style: { width: '100%' } }) }), _jsx(Form.Item, { name: "coupon_id", label: "\u05E7\u05D5\u05E4\u05D5\u05DF", rules: [{ required: true, message: 'קופון נדרש' }], children: _jsx(Select, { placeholder: "\u05D1\u05D7\u05E8 \u05E7\u05D5\u05E4\u05D5\u05DF", options: coupons.map((c) => ({
                            value: c.id,
                            label: `${c.code} · ${c.discount_percent}%${c.active ? '' : ' (לא פעיל)'}`,
                        })) }) })] }) }));
}
function RuleEditModal({ rule, onClose, onDone, coupons, }) {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    useEffect(() => {
        if (rule !== null) {
            form.setFieldsValue({
                fail_threshold: rule.fail_threshold,
                coupon_id: rule.coupon_id,
            });
        }
    }, [rule, form]);
    const submit = async () => {
        if (rule === null)
            return;
        let v;
        try {
            v = await form.validateFields();
        }
        catch {
            return;
        }
        setSubmitting(true);
        try {
            await updateUpsellRule(rule.id, {
                fail_threshold: Number(v.fail_threshold),
                coupon_id: Number(v.coupon_id),
            });
            notifySuccess('הכלל עודכן');
            onDone();
        }
        catch { /* client auto-surfaces */ }
        finally {
            setSubmitting(false);
        }
    };
    return (_jsx(Modal, { title: rule ? `עריכת כלל · ${rule.grade_name} · רמה ${rule.difficulty_level}` : 'עריכת כלל', open: rule !== null, onCancel: onClose, onOk: submit, okText: "\u05E9\u05DE\u05D5\u05E8", cancelText: "\u05D1\u05D8\u05DC", confirmLoading: submitting, destroyOnHidden: true, children: _jsxs(Form, { form: form, layout: "vertical", preserve: false, children: [_jsx(Form.Item, { name: "fail_threshold", label: "\u05E1\u05E3 \u05DB\u05E9\u05DC\u05D5\u05E0\u05D5\u05EA", rules: [{ required: true, message: 'סף כשלונות נדרש' }], children: _jsx(InputNumber, { min: 1, max: 50, style: { width: '100%' } }) }), _jsx(Form.Item, { name: "coupon_id", label: "\u05E7\u05D5\u05E4\u05D5\u05DF", rules: [{ required: true, message: 'קופון נדרש' }], children: _jsx(Select, { options: coupons.map((c) => ({
                            value: c.id,
                            label: `${c.code} · ${c.discount_percent}%${c.active ? '' : ' (לא פעיל)'}`,
                        })) }) })] }) }));
}
